import React, { useEffect, useRef, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import CustomCursor from './CustomCursor';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .iglow-root {
    font-family: 'Outfit', sans-serif;
    --red:        #EC003F;
    --red-light:  #FF4D7A;
    --red-dark:   #A8002D;
    --red-deep:   #5A0018;
    --red-pale:   #FECDD3;
    --bg:         #FFF0F3;
    background: var(--bg);
  }
  .iglow-root *, .iglow-root *::before, .iglow-root *::after {
    box-sizing: border-box;
    font-family: 'Outfit', sans-serif;
  }
  .iglow-root h1, .iglow-root h2, .iglow-root h3,
  .iglow-root h4, .iglow-root h5, .iglow-root h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  /* ── HERO ── */
  .iglow-hero {
    position: relative; overflow: hidden;
    background: linear-gradient(135deg, #2D0010 0%, #7C0022 50%, #5A0018 100%);
    min-height: 88vh; display: flex; align-items: center; justify-content: center;
  }

  .iglow-grid { position: absolute; inset: 0; pointer-events: none; perspective: 600px; }
  .iglow-grid-inner {
    position: absolute; bottom: -40px; left: -20%; right: -20%; height: 60%;
    background-image:
      linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px);
    background-size: 60px 60px;
    transform: rotateX(70deg); transform-origin: bottom center;
    animation: iglow-grid-scroll 8s linear infinite;
    mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
    -webkit-mask-image: linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 80%);
  }
  @keyframes iglow-grid-scroll { from { background-position: 0 0; } to { background-position: 0 60px; } }

  .iglow-orbit {
    position: absolute; inset: -48px; border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.25);
    animation: iglow-orbit3d 8s linear infinite; transform-style: preserve-3d;
  }
  .iglow-orbit-2 { inset: -72px; border-color: rgba(255,77,122,0.25); animation: iglow-orbit3d 12s linear infinite reverse; }
  @keyframes iglow-orbit3d {
    0%   { transform: rotateX(70deg) rotateZ(0deg); }
    100% { transform: rotateX(70deg) rotateZ(360deg); }
  }
  .iglow-orbit::after {
    content: ''; position: absolute; top: -4px; left: 50%;
    width: 8px; height: 8px; background: #fff; border-radius: 50%;
    box-shadow: 0 0 12px 3px rgba(255,255,255,0.9); transform: translateX(-50%);
  }
  .iglow-orbit-2::after { background: var(--red-pale); box-shadow: 0 0 12px 3px rgba(254,205,211,0.85); top: auto; bottom: -4px; }

  .iglow-particle { position: absolute; border-radius: 50%; pointer-events: none; animation: iglow-particle-float linear infinite; }
  .iglow-particle:nth-child(1) { width:4px; height:4px; background:rgba(236,0,63,0.9);   left:15%; top:20%; animation-duration:7s;  animation-delay:0s; }
  .iglow-particle:nth-child(2) { width:3px; height:3px; background:rgba(255,77,122,0.8); left:80%; top:60%; animation-duration:9s;  animation-delay:1s; }
  .iglow-particle:nth-child(3) { width:5px; height:5px; background:rgba(255,255,255,0.6);left:60%; top:15%; animation-duration:11s; animation-delay:2s; }
  .iglow-particle:nth-child(4) { width:3px; height:3px; background:rgba(254,205,211,0.7);left:30%; top:75%; animation-duration:8s;  animation-delay:3s; }
  .iglow-particle:nth-child(5) { width:4px; height:4px; background:rgba(236,0,63,0.8);   left:90%; top:30%; animation-duration:10s; animation-delay:0.5s; }
  @keyframes iglow-particle-float {
    0%   { transform: translateY(0) scale(1); opacity: 0; }
    10%  { opacity: 1; } 90% { opacity: 1; }
    100% { transform: translateY(-80px) scale(0.5); opacity: 0; }
  }

  .iglow-blob { position: absolute; border-radius: 50%; filter: blur(80px); pointer-events: none; }
  .iglow-blob-1 { width:500px; height:500px; background:rgba(200,0,50,0.45); top:-100px; right:-100px; animation:iglow-drift 10s ease-in-out infinite alternate; }
  .iglow-blob-2 { width:360px; height:360px; background:rgba(100,0,25,0.5); bottom:-80px; left:-60px; animation:iglow-drift 13s ease-in-out infinite alternate-reverse; }
  @keyframes iglow-drift { from { transform:translate(0,0) scale(1); } to { transform:translate(30px,20px) scale(1.08); } }

  .iglow-hero-content {
    position: relative; z-index: 10;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px; max-width: 1200px; width: 100%;
    padding: 120px 60px 80px 80px;
  }
  .iglow-image-wrap { flex-shrink: 0; position: relative; }

  .iglow-image-glow {
    position: absolute; inset: -24px;
    background: radial-gradient(circle, rgba(236,0,63,0.45) 0%, transparent 70%);
    border-radius: 50%; animation: iglow-glow-pulse 3s ease-in-out infinite;
  }
  @keyframes iglow-glow-pulse { 0%,100%{opacity:0.5;transform:scale(1);} 50%{opacity:1;transform:scale(1.06);} }

  .iglow-image-3d {
    transform-style: preserve-3d; transition: transform 0.1s ease;
    cursor: pointer; position: relative;
  }
  .iglow-image-3d::before {
    content: ''; position: absolute; inset: -2px; border-radius: 26px;
    background: linear-gradient(135deg, #fff, var(--red-pale), var(--red-light));
    z-index: 1; opacity: 0; transition: opacity 0.3s ease;
  }
  .iglow-image-3d:hover::before { opacity: 1; }
  .iglow-image-3d::after {
    content: ''; position: absolute; inset: 0; border-radius: 24px;
    background: linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0) 100%);
    background-size: 200% 200%; background-position: 200% 200%;
    z-index: 3; pointer-events: none; opacity: 0;
    transition: opacity 0.3s ease, background-position 0.4s ease;
  }
  .iglow-image-3d:hover::after { opacity: 1; background-position: -20% -20%; }

  .iglow-image {
    width: 380px; height: 380px; object-fit: cover; border-radius: 24px;
    position: relative; z-index: 2;
    box-shadow: 0 30px 80px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.2), 20px 20px 60px rgba(0,0,0,0.2);
    transition: box-shadow 0.3s ease;
  }
  .iglow-image-3d:hover .iglow-image {
    box-shadow: 0 50px 100px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.5),
      -20px 20px 60px rgba(168,0,45,0.35), 20px -10px 40px rgba(255,77,122,0.3);
  }

  .iglow-image-reflection {
    position: absolute; bottom: -20px; left: 10%; right: 10%; height: 30px;
    background: radial-gradient(ellipse, rgba(236,0,63,0.4) 0%, transparent 70%);
    filter: blur(8px); animation: iglow-reflection-pulse 3s ease-in-out infinite;
  }
  @keyframes iglow-reflection-pulse { 0%,100%{opacity:0.5;transform:scaleX(0.9);} 50%{opacity:0.9;transform:scaleX(1.05);} }

  .iglow-hero-text { flex: 1; padding-left: 40px; }

  .iglow-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase;
    color: rgba(255,255,255,0.85); margin-bottom: 16px;
    opacity: 0; animation: iglow-slideUp 0.8s ease forwards 0.3s;
  }
  .iglow-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(54px,7vw,82px); font-weight: 600; line-height: 1;
    color: white; margin: 0 0 8px;
    opacity: 0; animation: iglow-slideUp 0.8s ease forwards 0.5s;
  }
  .iglow-title-accent {
    display: block; font-family: 'Outfit', sans-serif;
    font-size: clamp(14px,1.8vw,18px); font-weight: 400;
    background: linear-gradient(90deg, #FECDD3, #FCA5A5);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
    margin-top: 6px; letter-spacing: 0.08em; text-transform: uppercase;
  }
  .iglow-divider {
    width: 60px; height: 2px;
    background: linear-gradient(90deg, #fff, rgba(255,255,255,0.3));
    margin: 24px 0;
    opacity: 0; animation: iglow-slideUp 0.8s ease forwards 0.7s;
  }
  .iglow-desc {
    font-family: 'Outfit', sans-serif;
    font-size: 17px; line-height: 1.78; font-weight: 300;
    color: rgba(255,255,255,0.85); max-width: 480px;
    opacity: 0; animation: iglow-slideUp 0.8s ease forwards 0.9s;
  }

  .iglow-back-btn-wrap {
    margin-top: 48px;
    opacity: 0; animation: iglow-slideUp 0.8s ease forwards 1.1s;
  }
  .iglow-back-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(255,255,255,0.3);
    color: rgba(255,255,255,0.75);
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .iglow-back-btn:hover {
    background: rgba(236,0,63,0.18);
    border-color: rgba(255,77,122,0.6);
    color: var(--red-pale);
    transform: translateX(-4px);
  }

  .site-link-btn {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 10px 22px; border-radius: 100px;
    background: rgba(255,255,255,0.07);
    border: 1px solid rgba(254,205,211,0.45);
    color: #FECDD3;
    font-family: 'Outfit', sans-serif;
    font-size: 0.78rem; font-weight: 500;
    letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, color 0.3s, transform 0.3s;
  }
  .site-link-btn:hover {
    background: rgba(254,205,211,0.18);
    border-color: #FECDD3;
    color: #fff;
    transform: translateX(-4px);
  }

  @keyframes iglow-slideUp { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }

  /* ── iGLOW IN ACTION (same style as iCoffee action-section) ── */
  .iglow-action-section {
    background: linear-gradient(180deg, #0F0005 0%, #2D0010 100%);
    padding: 100px 40px;
    position: relative; overflow: hidden;
  }
  .iglow-action-section::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(236,0,63,0.6), transparent);
  }
  .iglow-action-section::after {
    content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(254,205,211,0.3), transparent);
  }

  .iglow-action-inner {
    max-width: 1100px; margin: 0 auto;
    display: flex; flex-direction: row; align-items: center;
    gap: 80px;
  }

  /* Left text block */
  .iglow-action-text { flex: 1; min-width: 0; }
  .iglow-action-eyebrow {
    font-family: 'Outfit', sans-serif; font-size: 0.6rem; font-weight: 500;
    letter-spacing: 0.3em; text-transform: uppercase; color: var(--red-pale);
    display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
  }
  .iglow-action-eyebrow-line { display: inline-block; width: 32px; height: 1px; background: var(--red-light); }

  .iglow-action-heading {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(36px,5vw,60px); font-weight: 500; line-height: 1.1;
    color: white; margin: 0 0 8px;
  }
  .iglow-action-heading-italic {
    font-family: 'Cormorant Garamond', serif;
    font-style: italic; font-weight: 400;
    color: var(--red-pale); display: block; margin-top: 4px;
  }

  .iglow-action-divider {
    width: 48px; height: 1px;
    background: linear-gradient(90deg, var(--red-light), transparent);
    margin: 24px 0;
  }

  .iglow-action-desc {
    font-family: 'Outfit', sans-serif; font-weight: 300;
    font-size: 16px; line-height: 1.8; color: rgba(255,255,255,0.65);
    max-width: 420px; margin-bottom: 32px;
  }

  .iglow-action-yt-link {
    display: inline-flex; align-items: center; gap: 10px;
    padding: 12px 26px; border-radius: 100px;
    background: rgba(236,0,63,0.15);
    border: 1px solid rgba(236,0,63,0.45);
    color: var(--red-pale);
    font-family: 'Outfit', sans-serif; font-size: 0.78rem;
    font-weight: 500; letter-spacing: 0.08em; text-transform: uppercase;
    text-decoration: none;
    transition: background 0.3s, border-color 0.3s, transform 0.3s, box-shadow 0.3s;
  }
  .iglow-action-yt-link:hover {
    background: rgba(236,0,63,0.3);
    border-color: var(--red-pale);
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(90,0,24,0.5);
  }
  .iglow-action-yt-icon {
    width: 28px; height: 28px; background: #FF0000; border-radius: 6px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }

  /* Right: video card */
  .iglow-action-video-col { flex-shrink: 0; width: 460px; }

  .iglow-action-video-card {
    background: rgba(255,255,255,0.04); border: 1px solid rgba(236,0,63,0.28);
    border-radius: 16px; width: 100%; cursor: pointer;
    transition: background 0.3s, border-color 0.3s, box-shadow 0.3s, transform 0.3s;
    overflow: hidden;
  }
  .iglow-action-video-card:hover {
    background: rgba(236,0,63,0.12); border-color: rgba(236,0,63,0.6);
    transform: translateY(-6px) scale(1.02); box-shadow: 0 20px 48px rgba(90,0,24,0.45);
  }

  .iglow-action-video-thumb {
    position: relative; width: 100%; aspect-ratio: 16/9; overflow: hidden;
    background: rgba(90,0,24,0.4); display: flex; align-items: center; justify-content: center;
  }
  .iglow-action-video-thumb img {
    position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.85;
    transition: transform 0.4s ease;
  }
  .iglow-action-video-card:hover .iglow-action-video-thumb img { transform: scale(1.06); }

  .iglow-action-play-overlay {
    position: absolute; inset: 0; background: rgba(45,0,16,0.45);
    display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 10px;
    transition: background 0.3s;
  }
  .iglow-action-video-card:hover .iglow-action-play-overlay { background: rgba(90,0,24,0.6); }

  .iglow-action-play-btn {
    width: 56px; height: 56px; background: rgba(255,255,255,0.95); border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; padding-left: 4px; color: var(--red);
    box-shadow: 0 4px 20px rgba(90,0,24,0.5);
    transition: transform 0.3s, box-shadow 0.3s;
  }
  .iglow-action-video-card:hover .iglow-action-play-btn { transform: scale(1.15); box-shadow: 0 8px 30px rgba(236,0,63,0.6); }

  .iglow-action-yt-badge {
    background: #FF0000; color: white; font-family: 'Outfit', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.08em;
    padding: 3px 8px; border-radius: 4px; text-transform: uppercase;
  }

  .iglow-action-card-body { padding: 14px 18px 16px; text-align: left; }
  .iglow-action-card-name { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; margin-bottom: 3px; }
  .iglow-action-card-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(254,205,211,0.85); font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; line-height: 1.4; }
  .iglow-action-card-watch { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 5px; display: flex; align-items: center; gap: 4px; }

  /* ── VIDEO MODAL ── */
  .iglow-modal-backdrop {
    position: fixed; inset: 0; background: rgba(18,0,8,0.92); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 24px;
    animation: iglow-modal-fade 0.25s ease;
  }
  @keyframes iglow-modal-fade { from{opacity:0;} to{opacity:1;} }
  .iglow-modal-box {
    position: relative; width: 100%; max-width: 780px;
    background: #2D0010; border-radius: 24px;
    border: 1px solid rgba(255,77,122,0.3);
    box-shadow: 0 40px 120px rgba(0,0,0,0.85), 0 0 0 1px rgba(236,0,63,0.15);
    overflow: hidden; animation: iglow-modal-up 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  @keyframes iglow-modal-up { from{opacity:0;transform:scale(0.88) translateY(32px);} to{opacity:1;transform:scale(1) translateY(0);} }
  .iglow-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px 14px; border-bottom: 1px solid rgba(255,77,122,0.15);
  }
  .iglow-modal-title { font-family: 'Outfit', sans-serif; font-weight: 600; font-size: 15px; color: white; display: flex; align-items: center; gap: 8px; }
  .iglow-modal-badge {
    background: linear-gradient(135deg, var(--red-dark), var(--red-light));
    color: white; font-family: 'Outfit', sans-serif; font-size: 10px; font-weight: 600;
    letter-spacing: 0.1em; text-transform: uppercase; padding: 3px 10px; border-radius: 100px;
  }
  .iglow-modal-close {
    width: 32px; height: 32px; background: rgba(255,255,255,0.08); border: none;
    border-radius: 50%; color: rgba(255,255,255,0.7); font-size: 18px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.2s, color 0.2s; flex-shrink: 0;
  }
  .iglow-modal-close:hover { background: rgba(239,68,68,0.3); color: white; }
  .iglow-modal-frame { position: relative; width: 100%; aspect-ratio: 16/9; background: #000; }
  .iglow-modal-frame iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .iglow-modal-footer { padding: 14px 20px; display: flex; align-items: center; justify-content: space-between; gap: 10px; }
  .iglow-modal-yt-link {
    display: flex; align-items: center; gap: 6px;
    font-family: 'Outfit', sans-serif; font-size: 12px; font-weight: 600;
    color: rgba(255,255,255,0.5); text-decoration: none; transition: color 0.2s;
  }
  .iglow-modal-yt-link:hover { color: #FF4444; }
  .iglow-modal-topic { font-family: 'Outfit', sans-serif; font-size: 11px; color: rgba(254,205,211,0.7); letter-spacing: 0.06em; text-transform: uppercase; }

  @media (max-width: 900px) {
    .iglow-action-inner { flex-direction: column; gap: 48px; align-items: center; text-align: center; }
    .iglow-action-eyebrow { justify-content: center; }
    .iglow-action-desc { margin: 0 auto 32px; }
    .iglow-action-yt-link { margin: 0 auto; }
    .iglow-action-video-col { width: 100%; }
  }

  @media (max-width: 768px) {
    .iglow-hero-content { flex-direction:column; gap:40px; padding:100px 24px 60px; text-align:center; }
    .iglow-hero-text { padding-left:0; }
    .iglow-divider { margin:24px auto; }
    .iglow-desc { margin:0 auto; }
  }
     @media (max-width: 480px) {
    .iglow-hero-content { padding: 90px 16px 48px; gap: 32px; }
    .iglow-image { width: 240px; height: 240px; border-radius: 18px; }
    .iglow-title { font-size: clamp(44px,12vw,64px); }
    .iglow-title-accent { font-size: 13px; }
    .iglow-desc { font-size: 14px; }
    .iglow-back-btn , .iglow-site-link-btn{ font-size: 0.7rem; padding: 9px 16px; }
    .iglow-action-section { padding: 48px 16px; }
    .iglow-action-video-col { max-width: 100%; }
    .iglow-action-yt-link { padding: 10px 18px; font-size: 0.72rem; }
    .iglow-modal-backdrop { padding: 12px; }
    .iglow-modal-box { border-radius: 12px; }
    .iglow-modal-title { font-size: 13px; }
    .iglow-modal-badge { font-size: 9px; padding: 2px 8px; }
    .iglow-modal-close { width: 28px; height: 28px; font-size: 15px; }
  }
`;

const IGLOW_VIDEO_ID = 'CxbYM1QDHEk';

function AnimatedSection({ children }) {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{ opacity: visible?1:0, transform: visible?'none':'translateY(28px)', transition: 'opacity 0.7s ease, transform 0.7s ease' }}>
      {children}
    </div>
  );
}

const IGlow = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setActiveVideo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleMouseMove = (e) => {
    const el = imageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left, y = e.clientY - rect.top;
    const cx = rect.width / 2, cy = rect.height / 2;
    el.style.transform = `perspective(800px) rotateX(${-((y-cy)/cy)*18}deg) rotateY(${((x-cx)/cx)*18}deg) scale3d(1.06,1.06,1.06)`;
  };
  const handleMouseLeave = () => {
    if (imageRef.current) imageRef.current.style.transform = `perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)`;
  };

  return (
    <>
      <CustomCursor />
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="iglow-root min-h-screen flex flex-col">
        <Header />

        {/* ── Hero ── */}
        <section className="iglow-hero">
          <div className="iglow-grid"><div className="iglow-grid-inner" /></div>
          <div className="iglow-particle" /><div className="iglow-particle" /><div className="iglow-particle" />
          <div className="iglow-particle" /><div className="iglow-particle" />
          <div className="iglow-blob iglow-blob-1" />
          <div className="iglow-blob iglow-blob-2" />

          <div className="iglow-hero-content">
            <div className="iglow-image-wrap">
              <div className="iglow-image-glow" />
              <div className="iglow-orbit" />
              <div className="iglow-orbit iglow-orbit-2" />
              <div className="iglow-image-3d" ref={imageRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
                <img src="/assets/product_iglow.jpeg" alt="iGlow" className="iglow-image" />
              </div>
              <div className="iglow-image-reflection" />
            </div>

            <div className="iglow-hero-text">
              <p className="iglow-eyebrow">Ingestible Skincare Innovation</p>
              <h1 className="iglow-title">
                iGlow
                <span className="iglow-title-accent">Product Description</span>
              </h1>
              <div className="iglow-divider" />
              <p className="iglow-desc">
                iGlow is an ingestible skincare innovation that works at the cellular level to restore, rejuvenate, and reveal your natural radiance. Beauty that begins from within — because true glow starts at the source.
              </p>
              <div className="iglow-back-btn-wrap" style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <a href="/products" className="iglow-back-btn">← Back to Products</a>
                <a href="https://vivaiglow.com/" target="_blank" rel="noopener noreferrer" className="site-link-btn">
                  Visit iGlow Site ↗
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ── iGlow in Action ── */}
        <section className="iglow-action-section">
          <div className="iglow-action-inner">

            {/* Left: text */}
            <AnimatedSection>
              <div className="iglow-action-text">
                <p className="iglow-action-eyebrow">
                  <span className="iglow-action-eyebrow-line" />
                  YouTube · Watch Now
                </p>
                <h2 className="iglow-action-heading">
                  iGlow
                  <span className="iglow-action-heading-italic">in Action</span>
                </h2>
                <div className="iglow-action-divider" />
                <p className="iglow-action-desc">
                  Watch iGlow in action — discover how this ingestible skincare innovation works at the cellular level to restore radiance, reduce signs of ageing, and reveal your skin's natural glow from within.
                </p>
                <a
                  href={`https://www.youtube.com/watch?v=${IGLOW_VIDEO_ID}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="iglow-action-yt-link"
                >
                  <span className="iglow-action-yt-icon">
                    <svg width="14" height="10" viewBox="0 0 14 10" fill="white">
                      <path d="M5.5 7.5v-5l4.5 2.5-4.5 2.5z"/>
                    </svg>
                  </span>
                  Watch on YouTube
                </a>
              </div>
            </AnimatedSection>

            {/* Right: video card */}
            <AnimatedSection>
              <div className="iglow-action-video-col">
                <div
                  className="iglow-action-video-card"
                  onClick={() => setActiveVideo({ youtubeId: IGLOW_VIDEO_ID, lang: 'iGlow in Action', topic: 'Skin Glow · Anti-Ageing · Cellular Rejuvenation' })}
                >
                  <div className="iglow-action-video-thumb">
                    <img
                      src={`https://i.ytimg.com/vi/${IGLOW_VIDEO_ID}/hqdefault.jpg`}
                      alt="iGlow in Action"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="iglow-action-play-overlay">
                      <div className="iglow-action-play-btn">▶</div>
                      <span className="iglow-action-yt-badge">YouTube</span>
                    </div>
                  </div>
                  <div className="iglow-action-card-body">
                    <div className="iglow-action-card-name">iGlow in Action</div>
                    <div className="iglow-action-card-topic">Skin Glow · Anti-Ageing · Cellular Rejuvenation</div>
                    <div className="iglow-action-card-watch"><span>▶</span> Watch Video</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>

          </div>
        </section>

        {/* ── Video Modal ── */}
        {activeVideo && (
          <div className="iglow-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setActiveVideo(null); }}>
            <div className="iglow-modal-box">
              <div className="iglow-modal-header">
                <div className="iglow-modal-title">
                  <span>iGlow</span>
                  <span className="iglow-modal-badge">{activeVideo.lang}</span>
                </div>
                <button className="iglow-modal-close" onClick={() => setActiveVideo(null)}>✕</button>
              </div>
              <div className="iglow-modal-frame">
                <iframe
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                  title={`iGlow — ${activeVideo.lang}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="iglow-modal-footer">
                <span className="iglow-modal-topic">{activeVideo.topic}</span>
                <a href={`https://www.youtube.com/watch?v=${activeVideo.youtubeId}`} target="_blank" rel="noopener noreferrer" className="iglow-modal-yt-link">
                  <span style={{ fontSize:'14px' }}>▶</span> Open on YouTube
                </a>
              </div>
            </div>
          </div>
        )}
      {/* <TestimonyUpload/> */}
        <Footer />
      </div>
    </>
  );
};

export default IGlow;